import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePagination, useTable } from 'react-table'
import moment from 'moment'
import _ from 'lodash'
import { useSearchParams } from 'react-router-dom'
import Select, { components } from 'react-select'
import cx from 'classnames'
import toast from 'react-hot-toast'

import api from '../api'
import { getParams } from 'LegacyUtils/params'
import ReactDatePicker from 'react-datepicker'

const ITEMS_PER_PAGE = 10

const REVIEW_STATUS = [
  { label: 'Pending', value: 'pending' },
  { label: 'Content Required', value: 'content_required' },
  { label: 'Training Required', value: 'training_required' },
  { label: 'Trained', value: 'trained' },
  { label: 'Ignore', value: 'ignore' }
]

const FEEDBACK_FILTER_OPTIONS = [
  { label: 'Yes', value: 'helpful' },
  { label: 'No', value: 'not_helpful' }
]

const CheckBoxOption = (props) => {
  const handleChange = _.noop

  return (
    <components.Option {...props} className={cx(props.className, 'input-checkbox')}>
      <div className='filter-checkbox'>
        <input type='checkbox' checked={props.isSelected} onChange={handleChange} />
        <label>{props.label}</label>
      </div>
    </components.Option>
  )
}

const CheckBoxGroup = (props) => {
  const groupOptionValues = props.options.map((opt) => opt.value)
  const allSelected = props.children.every((opt) => opt.props.isSelected)

  const allValues = props.getValue()

  const optionsToSelect = useMemo(() => {
    const valuesWithoutGroupOptions = allValues.filter((opt) => !groupOptionValues.includes(opt.value))

    if (allSelected) {
      return valuesWithoutGroupOptions
    }

    return [...valuesWithoutGroupOptions, ...props.options]
  }, [allSelected, allValues, groupOptionValues, props.options])

  const toggleAllSelection = () => props.selectProps.onChange(optionsToSelect)

  return <components.Group {...props} headingProps={{ ...props.headingProps, allSelected, toggleAllSelection }} />
}

const MultiValue = (props) => {
  if (props.index !== 0) {
    return null
  }

  const noOfItemSelected = props.selectProps.value.length
  return `${noOfItemSelected} Review status selected`
}

const DatePicker = (props) => {
  const { onChange = _.noop, value = '' } = props
  const hiddenRef = useRef()

  return (
    <Fragment>
      <div className='hidden' contentEditable='true' ref={hiddenRef} />
      <Select
        placeholder='Date Range'
        isMulti={false}
        isClearable
        isSearchable={false}
        classNamePrefix='filter'
        closeMenuOnSelect={false}
        value={value}
        onChange={onChange}
        components={{
          ValueContainer: components.ValueContainer,
          MenuList: ({ selectProps, setValue }) => {
            const handleChange = (dates) => {
              const [start, end] = dates
              const startDate = moment(start)
              let startFormatted = startDate.format('DD-MMM-YYYY')

              let formattedDate = startFormatted
              let dateValue = startDate.format()

              if (end) {
                const endDate = moment(end)

                if (startDate.year() === endDate.year()) {
                  startFormatted = startDate.format('DD-MMM')
                }

                const endFormatted = endDate.format('DD-MM-YYYY')
                dateValue += '~' + endDate.format()
                formattedDate += '~' + endFormatted
              }

              setValue({ value: dateValue, label: formattedDate })
              if (end) {
                hiddenRef.current.focus()
              }
            }

            const dateValue = selectProps.value.value
            const startDate = dateValue && new Date(dateValue.split('~')[0])
            const endDate = dateValue && dateValue.split('~')[1] && new Date(dateValue.split('~')[1])

            return (
              <ReactDatePicker
                inline
                selectsRange
                onChange={handleChange}
                startDate={startDate || null}
                endDate={endDate || null}
                maxDate={new Date()}
              />
            )
          }
        }}
      />
    </Fragment>
  )
}

const Training = () => {
  const params = getParams(null)

  const [isLoading, setIsLoading] = useState(true)
  const [serverPageInfo, setServerPageInfo] = useState(null)
  const [trainingList, setTrainingList] = useState([])
  const [isHelpfulFilter, setIsHelpfulFilter] = useState(params.get('helpful'))
  const [reviewStatusFilter, setReviewStatusFilter] = useState(params.get('review_status', ''))
  const [dateRangeFilter, setDateRangeFilter] = useState(params.get('date_range', ''))

  const [, setSearchParams] = useSearchParams()

  const columns = useMemo(() => {
    return [
      {
        Header: 'Search Phrase',
        accessor: 'data.question_asked_by_visitor'
      },
      {
        Header: 'Suggestions shown',
        accessor: (row) => row,
        Cell: ({ cell: { value } }) => {
          return (
            <ul>
              {value.data.suggestions_given_to_visitor?.map((suggestion) => {
                return <li key={'Suggestion' + suggestion.value}>{suggestion.text}</li>
              })}
            </ul>
          )
        }
      },
      {
        Header: 'Suggestions selected',
        accessor: 'data.suggestion_selected_by_visitor'
      },
      {
        Header: 'Helpful',
        accessor: (row) => row,
        Cell: ({ cell: { value } }) => {
          const { feedback_selected_by_visitor: selectedFeedback } = value.data

          switch (selectedFeedback?.value) {
            case 'helpful':
              return 'Yes'

            case 'not_helpful':
              return 'No'

            default:
              return '-'
          }
        }
      },
      {
        Header: 'Created On',
        accessor: (row) => row,
        Cell: ({ cell: { value } }) => {
          return moment(value.created_at).format('DD-MMM-YYYY')
        }
      },
      {
        Header: 'Review Status',
        accessor: (row) => row,
        Cell: ({ cell: { value }, setReviewStatus }) => {
          const handleChange = (e) => {
            setReviewStatus(value.id, e.target.value)
          }

          return (
            <select className='review-select' value={value.data.review_status ?? ''} onChange={handleChange}>
              <option value='' disabled>
                Select
              </option>
              {REVIEW_STATUS.map((r) => {
                return (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                )
              })}
            </select>
          )
        }
      }
    ]
  }, [])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    gotoPage,
    state: { pageIndex, pageSize }
  } = useTable(
    {
      columns,
      data: trainingList,
      initialState: { pageIndex: params.get('page') ? parseInt(params.get('page')) - 1 : 0, pageSize: ITEMS_PER_PAGE },
      manualPagination: true,
      pageCount: serverPageInfo?.total ? Math.ceil(serverPageInfo?.total / ITEMS_PER_PAGE) : null,
      autoResetHiddenColumns: false
    },
    usePagination
  )

  const currentPage = pageIndex + 1

  const getTrainingData = useCallback((payload = {}, showToast = false) => {
    setIsLoading(true)

    const options = {
      params: {
        offset: (payload.page - 1) * ITEMS_PER_PAGE,
        limit: ITEMS_PER_PAGE,
        helpful: payload.helpful
      }
    }

    if (payload.date_range?.split('~').length === 2) {
      options.params.start_date = moment(payload.date_range.split('~')[0]).format('MM-DD-YYYY')
      options.params.end_date = moment(payload.date_range.split('~')[1]).format('MM-DD-YYYY')
    }

    const reviewStatus = payload.review_status?.split(',').filter(Boolean) || []
    if (reviewStatus.length > 0) {
      options.params.review_status = reviewStatus
    }

    const promise = api
      .get('v1/training', options)
      .then(({ data }) => {
        setTrainingList(data.results)
        setServerPageInfo(data.meta)
      })
      .finally(() => {
        setIsLoading(false)
      })

    if (showToast) {
      toast.promise(
        promise,
        {
          loading: 'Fetching data...',
          error: 'Error while fetching training data.'
        },
        {
          success: {
            style: {
              display: 'none'
            }
          }
        }
      )
    }
  }, [])

  const debouncedSearch = useMemo(() => {
    return _.debounce(getTrainingData, 500)
  }, [getTrainingData])

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  const fetchResults = (filter = {}, showToast = false) => {
    const payload = { ...params.toJSON(), ...filter }
    debouncedSearch(payload, showToast)
  }

  useEffect(() => {
    const newParams = {
      page: currentPage
    }

    if (isHelpfulFilter !== null) {
      newParams.helpful = isHelpfulFilter
    }

    if (reviewStatusFilter) {
      newParams.review_status = reviewStatusFilter
    }

    if (dateRangeFilter) {
      newParams.date_range = dateRangeFilter
    }

    setSearchParams(newParams, { replace: true })
  }, [currentPage, isHelpfulFilter, setSearchParams, reviewStatusFilter, dateRangeFilter])

  useEffect(() => {
    fetchResults({ page: currentPage })
  }, [currentPage]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleFeedbackFilterChange = (v) => {
    gotoPage(0)
    const isHelpful = v ? v.value === 'helpful' : null
    setIsHelpfulFilter(isHelpful)
    fetchResults({ helpful: isHelpful }, true)
  }

  const handleReviewStatusChange = (v) => {
    gotoPage(0)
    const status = v.map((stat) => stat.value).join(',')
    setReviewStatusFilter(status)
    fetchResults({ review_status: status }, true)
  }

  const handleDateRangeChange = (v) => {
    gotoPage(0)
    setDateRangeFilter(v?.value ?? null)
    if (v?.value.split('~').length === 2) {
      fetchResults({ date_range: v.value }, true)
    } else {
      const showToast = v?.value ? v?.value.split('~').length === 2 : true
      fetchResults({ date_range: '' }, showToast)
    }
  }

  const setReviewStatus = useCallback((id, status) => {
    const promise = api
      .patch(`v1/training/${id}/review-status`, { status })
      .then(({ data }) => {
        setTrainingList((previousList) => {
          return previousList.map((item) => {
            if (item.id === data.id) {
              return data
            }

            return item
          })
        })
      })
      .catch((err) => {
        console.error(`Unable to update review for ${id}`, err)
      })

    toast.promise(promise, {
      loading: 'Updating review status',
      success: 'Review status updated successfully.',
      error: 'Error while fetching training data.'
    })
  }, [])

  const getDatePickerValue = () => {
    if (!dateRangeFilter) {
      return undefined
    }

    const startDate = moment(dateRangeFilter.split('~')[0])
    let startFormatted = startDate.format('DD-MMM-YYYY')

    let label = startFormatted

    if (dateRangeFilter.split('~')[1]) {
      const endDate = moment(dateRangeFilter.split('~')[1])
      const endFormatted = endDate.format('DD-MMM-YYYY')

      if (startDate.year() === endDate.year()) {
        startFormatted = moment(startDate).format('DD-MMM')
      }

      label += ' - ' + endFormatted
    }

    return {
      label,
      value: dateRangeFilter
    }
  }

  return (
    <div className='training-view'>
      <div className='page-top-bar'>
        <div className='title'>Search Phrases</div>
        <div className='right'>
          <div className='filters'>
            <div className='filter date_range'>
              <DatePicker value={getDatePickerValue()} onChange={handleDateRangeChange} />
            </div>
            <div className='filter review_status'>
              <Select
                options={REVIEW_STATUS}
                isMulti
                placeholder='Review Status'
                classNamePrefix='filter'
                isClearable
                isSearchable={false}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                value={REVIEW_STATUS.filter((status) => {
                  return reviewStatusFilter.split(',').includes(status.value)
                })}
                onChange={handleReviewStatusChange}
                components={{
                  Option: CheckBoxOption,
                  Group: CheckBoxGroup,
                  MultiValue
                }}
              />
            </div>
            <div className='filter feedback'>
              <Select
                options={FEEDBACK_FILTER_OPTIONS}
                isMulti={false}
                isClearable
                isSearchable={false}
                placeholder='Helpful'
                classNamePrefix='filter'
                value={FEEDBACK_FILTER_OPTIONS.find((feed) => {
                  if (isHelpfulFilter !== null) {
                    return feed.value === (isHelpfulFilter ? 'helpful' : 'not_helpful')
                  }

                  return false
                })}
                onChange={handleFeedbackFilterChange}
                components={{
                  SingleValue: (props) => {
                    return <components.SingleValue {...props}>Helpful: {props.children}</components.SingleValue>
                  }
                }}
              />
            </div>
          </div>
          <div className='page-info'>
            {trainingList.length === 0 && '0 Results'}
            {trainingList.length > 0 && (
              <span>
                {currentPage * pageSize - pageSize + 1}-
                {currentPage * pageSize > serverPageInfo?.total ? serverPageInfo?.total : currentPage * pageSize} of{' '}
                {serverPageInfo?.total}
              </span>
            )}
          </div>
          <div className='pagination'>
            <button
              className='previous-button pagination-button mr-1'
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
            >
              ‹
            </button>{' '}
            <button className='next-button pagination-button' onClick={() => nextPage()} disabled={!canNextPage}>
              ›
            </button>{' '}
          </div>
        </div>
      </div>
      <div className='training-list'>
        <div className='table-style-1'>
          <table className='table' {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => {
                const { key } = headerGroup.getHeaderGroupProps()
                return (
                  <tr key={key} {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => {
                      const { key } = column.getHeaderProps()
                      return (
                        <th {...column.getHeaderProps()} key={key}>
                          {column.render('Header')}
                        </th>
                      )
                    })}
                  </tr>
                )
              })}
            </thead>

            <tbody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row)
                const { key, ...rowProps } = row.getRowProps()

                return (
                  <tr {...rowProps} key={key}>
                    {row.cells.map((cell) => {
                      const { key, ...cellProps } = cell.getCellProps()
                      return (
                        <td {...cellProps} key={key}>
                          {cell.render('Cell', { setReviewStatus })}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
          {trainingList.length === 0 && !isLoading && <p className='my-3 text-center'>No data found...</p>}
          {trainingList.length === 0 && isLoading && <p className='my-3 text-center'>Fetching Content...</p>}
        </div>
      </div>
    </div>
  )
}

export default Training
